import { once } from 'lodash-es';
import customerAccountsService from 'Assets/js/components/customerAccountsService';

const useCustomerAccounts = () => customerAccountsService.enabled();

const userInitB2B = (context, responseData, forceRefresh = false) => {
  // Get further B2B data asynchronously, delaying to avoid rendering of customer area
  setTimeout(() => {
    context.getters.userInstance.getUserB2BInfo(
      responseData,
      (responseDataB2B) => {
        if (
          !forceRefresh &&
          context.getters.feature.user?.b2b &&
          responseDataB2B?.b2b !== null &&
          responseDataB2B?.b2b !== undefined &&
          responseDataB2B?.b2b !== '' &&
          responseDataB2B?.b2b !== false
        ) {
          context.dispatch('dumpB2bCarts').then(() => {});
        }

        if (Array.isArray(responseDataB2B?.b2bUserList)) {
          context.commit('setUser', responseDataB2B);
        }

        if (context.getters.feature.user?.b2b) {
          context.commit('setB2B', responseDataB2B.b2b);
        }
        context.commit('setMenuLoaded', true);

        // Retrieve target path, based on user being B2B or not
        const targetPath =
          context.getters.feature.user?.b2b && responseDataB2B.b2b
            ? '/account/b2b-contacts/'
            : '/account/details/';
        const currentPath = window.location?.pathname || '';
        if (currentPath !== targetPath) {
          // Redirect to B2B contacts page
          window.location.href = targetPath;
        }
      }
    );
  }, 500);
};

const customerAccountsInit = async (
  context,
  callback = null,
  forceRefresh = false
) => {
  try {
    const responseData = await customerAccountsService.getMe();

    if (responseData) {
      context.commit('setFirstName', responseData.firstName);
      context.commit('setLastName', responseData.lastName);
      context.commit('setEmail', responseData.email);
      context.commit('setWishlist', responseData.wishlist?.value ?? []);
      context.commit('setUser', responseData);

      if (context.getters.feature.user?.b2b) {
        userInitB2B(context, responseData, forceRefresh);
      } else {
        context.commit('setMenuLoaded', true);
      }
    } else {
      context.commit('setMenuLoaded', true);
    }

    if (typeof callback === 'function') {
      callback();
    }
  } catch (error) {
    context.commit('setMenuLoaded', true);
    if (typeof callback === 'function') {
      callback();
    }
  }
};

const runUserInit = (context, payload) => {
  const { forceRefresh, callback } = payload || {};

  if (useCustomerAccounts()) {
    customerAccountsInit(context, callback, !!forceRefresh);
    return;
  }

  if (!context.getters.firstName || forceRefresh) {
    context.getters.userInstance.getUser(
      context.getters.cartInstance,
      (responseData) => {
        if (responseData) {
          if (
            !forceRefresh &&
            context.getters.feature.user?.b2b &&
            responseData.b2b !== null &&
            responseData.b2b !== undefined &&
            responseData.b2b !== '' &&
            responseData.b2b !== false
          ) {
            context.dispatch('dumpB2bCarts').then(() => {});
          }

          context.commit('setFirstName', responseData.firstName);
          context.commit('setLastName', responseData.lastName);
          context.commit('setEmail', responseData.email);
          context.commit('setWishlist', responseData.wishlist?.value ?? []);
          context.commit('setUser', responseData);

          // Get further B2B data asynchronously, delaying to avoid rendering of customer area
          userInitB2B(context, responseData);
        } else {
          // eslint-disable-next-line no-console
          console.warn('[STORE] userInit: empty response from getUser');
        }

        if (typeof callback === 'function') {
          callback();
        }
      }
    );
    return;
  }

  if (typeof callback === 'function') {
    callback();
  }
};

const actions = {
  setLoaded(context) {
    context.commit('setLoadedUser', true);
  },
  userLogout(context, router) {
    context.commit('logoutState');
  },
  setAddedContacts(context) {
    // eslint-disable-next-line no-console
    console.log('setAddedContacts - Timeout set');
    context.commit('setAddedContacts', true);
    setTimeout(() => {
      // eslint-disable-next-line no-console
      console.log('setAddedContacts - Timed out');
      context.commit('setAddedContacts', false);
    }, 120000);
  },
  deleteAddress(context, addressId) {
    if (useCustomerAccounts()) {
      return customerAccountsService
        .deleteAddress(addressId)
        .then((response) => {
          if (response?.user) {
            context.commit('setUser', response.user);
          }

          if (response?.errors?.length) {
            window.dispatchEvent(
              new CustomEvent('error-toggled', {
                detail: {
                  subtitle: 'Error',
                  title: 'Error occurred while deleting address',
                  content: response.errors[0].message
                }
              })
            );
          }

          return response;
        })
        .catch((error) => {
          window.dispatchEvent(
            new CustomEvent('error-toggled', {
              detail: {
                subtitle: 'Error',
                title: 'Error occurred while deleting address',
                content:
                  error?.response?.data?.errors?.[0]?.message ||
                  'Address delete failed. Please try again.'
              }
            })
          );
          return null;
        });
    }

    context.getters.userInstance.removeAddress(addressId);
    context.dispatch('userRefresh').then(() => {});
  },
  /**
   * @param context
   * @param {Object} payload
   */
  userInit: once((context, payload) => {
    runUserInit(context, payload);
  }),
  userRefresh(context, payload) {
    runUserInit(context, { ...(payload || {}), forceRefresh: true });
  },
  userAuthToken(context) {
    const token = btoa(context.getters.email);
    context.commit('[STORE] addToken', token);
    return token;
  },
  validateAndUseAuthToken(context, token) {
    context.commit('addToken');
    // eslint-disable-next-line no-console
    console.log('[STORE] validateAndUseAuthToken', [
      token,
      atob(token),
      context.getters.email
    ]);

    if (atob(token) === context.getters.email) {
      context.commit('removeToken');
      return true;
    }
    return false;
  },
  getCart(context) {
    if (!context.getters.isB2B) {
      context.commit('getCart');
      return context.getters.cart;
    }
  },
  userUpdate(context, payload) {
    if (useCustomerAccounts()) {
      const { user, onSuccess = null, onError = null } = payload || {};

      return new Promise((resolve) => {
        customerAccountsService
          .updateMe(user)
          .then((response) => {
            if (response?.errors?.length) {
              if (typeof onError === 'function') {
                onError(response.errors);
              }
              resolve({ ok: false, errors: response.errors });
              return;
            }

            if (response?.user) {
              context.commit('setUser', response.user);
              context.commit('setFirstName', response.user.firstName);
              context.commit('setLastName', response.user.lastName);
              context.commit('setEmail', response.user.email);
              context.commit(
                'setWishlist',
                response.user.wishlist?.value ?? []
              );
            }

            if (typeof onSuccess === 'function') {
              onSuccess();
            }

            resolve({ ok: true, responseData: response?.user });
          })
          .catch((error) => {
            const responseErrors = error?.response?.data?.errors;
            const errors =
              Array.isArray(responseErrors) && responseErrors.length
                ? responseErrors
                : [
                    {
                      field: ['user', 'general'],
                      message: 'Update failed. Please try again.'
                    }
                  ];

            if (typeof onError === 'function') {
              onError(errors);
            }

            resolve({ ok: false, errors });
          });
      });
    }

    const { user, onSuccess = null, onError = null } = payload || {};
    return new Promise((resolve) => {
      if (!user) {
        const errors = [
          {
            field: ['user', 'general'],
            message: 'User data is not available yet.'
          }
        ];
        if (typeof onError === 'function') {
          onError(errors);
        }
        resolve({ ok: false, errors });
        return;
      }

      let finished = false;
      const finish = (result) => {
        if (finished) {
          return;
        }
        finished = true;
        resolve(result);
      };

      const timeoutId = setTimeout(() => {
        const errors = [
          {
            field: ['user', 'general'],
            message: 'Update timed out. Please try again.'
          }
        ];
        if (typeof onError === 'function') {
          onError(errors);
        }
        finish({ ok: false, errors });
      }, 15000);

      context.getters.userInstance.updateUser(user, (responseData, errors) => {
        clearTimeout(timeoutId);
        if (errors && errors.length) {
          if (typeof onError === 'function') {
            onError(errors);
          }
          finish({ ok: false, errors });
          return;
        }

        if (responseData) {
          context.commit('setUser', responseData);
          context.commit('setFirstName', responseData.firstName);
          if (typeof onSuccess === 'function') {
            onSuccess();
          }
          finish({ ok: true, responseData });
          return;
        }

        const fallbackErrors = [
          {
            field: ['user', 'general'],
            message: 'Update failed. Please try again.'
          }
        ];
        if (typeof onError === 'function') {
          onError(fallbackErrors);
        }
        finish({ ok: false, errors: fallbackErrors });
      });
    });
  },
  userUpdateAddress(context, address) {
    if (useCustomerAccounts()) {
      return customerAccountsService
        .saveAddress(address)
        .then((response) => {
          if (response?.user) {
            context.commit('setUser', response.user);
          }

          return response;
        })
        .catch((error) => ({
          ok: false,
          errors: error?.response?.data?.errors || [
            {
              field: ['address', 'general'],
              message: 'Address update failed. Please try again.'
            }
          ]
        }));
    }

    // eslint-disable-next-line no-new
    new Promise((resolve) => {
      context.getters.userInstance.updateAddress(
        address.id,
        address,
        (response) => {
          resolve(response);
          if (
            response.data.data.customerAddressCreate.customerUserErrors.length >
            0
          ) {
            window.dispatchEvent(
              new CustomEvent('error-toggled', {
                detail: {
                  subtitle: 'Error',
                  title: 'Error occored while updating address',
                  content: `${response.data.data.customerAddressCreate.customerUserErrors[0].message}`
                }
              })
            );
          }
          context.dispatch('userRefresh');
        }
      );
    });
  }
};

export default actions;
